<template>
  <div>
    <v-app-bar
      app
      clipped-left
      flat
      dark
      class="primaryGradientBackground"
      :class="{
        'smallButtonPadding': $vuetify.breakpoint.xsOnly
      }"
    >
      <div
        class="app-bar-brand"
        :class="{ 'app-bar-brand--workspace': showNavigationDrawer && drawer }"
      >
        <v-app-bar-nav-icon
          v-if="showNavigationDrawer"
          @click.stop="drawer = !drawer"
        />
        <v-btn
          v-if="$vuetify.breakpoint.smAndUp"
          text
          class="brand-logo-btn px-2"
          :small="$vuetify.breakpoint.mdAndDown"
          @click="openArticles"
        >
          <BrandLogo />
        </v-btn>
      </div>
      <TopBarInternalLinks
        :article-step="articleStep"
        :disable-save="disableSave"
        class="app-bar-main"
      />
      <v-spacer />
      <div class="app-bar-end">
        <v-btn
          v-if="$store.getters.authenticated && !$store.getters.sharePreview"
          depressed
          rounded
          color="ai"
          class="text-none lucy-bar-btn"
          :small="$vuetify.breakpoint.xsOnly"
          :aria-label="$t('lucy.ask')"
          @click="openLucy"
        >
          <span class="lucy-bar-btn__mark">L</span>
          <span v-if="$vuetify.breakpoint.smAndUp" class="lucy-bar-btn__label">{{ $t('lucy.ask') }}</span>
        </v-btn>
        <AppBarUserInfo />
        <TopBarExternalLinks />
        <v-tooltip
          nudge-bottom="10px"
          bottom
        >
          <template #activator="{ on }">
            <v-btn
              icon
              class="app-bar-icon-btn"
              :aria-label="$t('topBar.settingsTooltip')"
              v-on="on"
              @click="customizerOpen = !customizerOpen"
            >
              <v-icon>settings</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('topBar.settingsTooltip') }}</span>
        </v-tooltip>
        <LanguageSelector />
        <v-tooltip
          v-if="$store.getters.authenticated && !$store.getters.sharePreview"
          nudge-bottom="10px"
          bottom
        >
          <template #activator="{ on }">
            <v-btn
              icon
              class="app-bar-icon-btn"
              :aria-label="$t('logout')"
              v-on="on"
              @click="logout"
            >
              <v-icon>logout</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('logout') }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>

    <ThemeCustomizer v-model="customizerOpen" />
    <LucyPanel />

    <NavigationDrawer
      v-if="showNavigationDrawer"
      v-model="drawer"
      :article-step="articleStep"
      :seo-score-context="seoScoreContext"
      :disable-save="disableSave"
    />
    <v-main>
      <v-container fluid>
        <v-progress-linear
          v-if="$store.getters.user === undefined && !isShareWorkspace"
          color="accent"
          indeterminate
        />
        <div v-else>
          <template v-if="$store.getters.authenticated || isShareWorkspace">
            <slot />
            <v-footer
              v-if="showFooter"
              padless
              class="mt-10"
            >
              <SubscriptionInfoList />
            </v-footer>
          </template>
          <!-- Login layout -->
          <div v-else class="login-layout" :class="{ 'login-layout--wide': $vuetify.breakpoint.lgAndUp }">
            <!-- Left: two feature cards (desktop only) -->
            <div v-if="$vuetify.breakpoint.lgAndUp" class="login-features-col">
              <LoginFeatures />
            </div>

            <!-- Right: login card -->
            <div class="login-card-col">
              <v-card class="login-card" rounded="xl" elevation="0">
                <div class="login-card-header primaryGradientBackground">
                  <BrandLogo :height="36" />
                  <p class="login-card-tagline mb-0 mt-2">{{ $t('loginTagline') }}</p>
                </div>
                <div class="login-card-body">
                  <FirebaseAuthUi />
                </div>
              </v-card>
            </div>

            <!-- Mobile: condensed feature list below login card -->
            <div v-if="$vuetify.breakpoint.mdAndDown" class="mt-6 w-100">
              <LoginFeatures />
            </div>
          </div>
        </div>
      </v-container>
    </v-main>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import LanguageSelector from '@/components/LanguageSelector.vue';
    import FirebaseAuthUi from "@/components/FirebaseAuthUi.vue";
    import AppBarUserInfo from "@/components/account/AppBarUserInfo.vue";
    import NavigationDrawer from "@/components/NavigationDrawer.vue";
    import TopBarExternalLinks from "@/components/TopBarExternalLinks.vue";
    import TopBarInternalLinks from "@/components/TopBarInternalLinks.vue";
    import SubscriptionInfoList from "@/components/subscriptions/SubscriptionInfoList.vue";
    import LoginFeatures from "@/components/subscriptions/LoginFeatures.vue";
    import ThemeCustomizer from "@/components/ThemeCustomizer.vue";
    import BrandLogo from '@/components/BrandLogo.vue';
    import LucyPanel from '@/components/lucy/LucyPanel.vue';
    import {SeoScoreContext, seoScoreContext} from "@/model/SeoScore";
    import {EventBus} from '@/utils/EventBus';
    import {openLucyPanel} from '@/service/lucyCoach';

    export default Vue.extend({
        name: 'UserLayout',
        components: {
            SubscriptionInfoList,
            LoginFeatures,
            TopBarInternalLinks,
            ThemeCustomizer,
            TopBarExternalLinks,
            NavigationDrawer,
            AppBarUserInfo,
            FirebaseAuthUi,
            LanguageSelector,
            BrandLogo,
            LucyPanel
        },
        props: {
            articleStep: {
                type: Number,
                default: null
            },
            seoScoreContext: {
                type: Object as PropType<SeoScoreContext>,
                default: () => seoScoreContext
            },
            disableSave: {
                type: Boolean,
                default: false
            }
        },
        data: function () {
            return {
                drawer: this.$vuetify.breakpoint.lgAndUp,
                customizerOpen: false,
                onEscapeKey: null as null | ((event: KeyboardEvent) => void),
            };
        },
        computed: {
            showFooter(): boolean {
                return this.$route.name === 'Home'
                    && this.$store.getters.user !== undefined
                    && this.$store.getters.planLoaded
                    && !this.$store.getters.hasPlan;
            },
            showNavigationDrawer(): boolean {
                if (this.$route.name === 'SharedArticle') {
                    return true;
                }
                if (!this.$store.getters.authenticated) {
                    return true;
                }
                return this.$route.name === 'Article';
            },
            isShareWorkspace(): boolean {
                return this.$route.name === 'SharedArticle'
                    || (this.$route.name === 'SeoScoreDetail' && !!this.$route.query.share);
            }
        },
        watch: {
            drawer() {
                this.$store.commit("setDrawer", this.drawer);
            }
        },
        created() {
            EventBus.$on('workspace-drawer-open', this.openWorkspaceDrawer);
            this.onEscapeKey = (event: KeyboardEvent) => {
                if (event.key !== 'Escape') {
                    return;
                }
                if (this.customizerOpen) {
                    this.customizerOpen = false;
                }
                EventBus.$emit('lucy-panel-close');
            };
            window.addEventListener('keydown', this.onEscapeKey);
        },
        destroyed() {
            EventBus.$off('workspace-drawer-open', this.openWorkspaceDrawer);
            if (this.onEscapeKey) {
                window.removeEventListener('keydown', this.onEscapeKey);
            }
        },
        methods: {
            openWorkspaceDrawer(): void {
                this.drawer = true;
            },
            openLucy(): void {
                openLucyPanel({question: 'next'});
            },
            openArticles(): void {
                if (this.$store.getters.sharePreview) {
                    this.$store.commit('clearSharePreview');
                    this.$router.push({name: 'Home'});
                    return;
                }
                if (this.$route.path !== '/') {
                    this.$router.push('/');
                }
            },
            logout(): void {
                if (this.$route.path.split('/')[1] === 'article') {
                    EventBus.$emit('onArticleLeave', () => {
                        this.$store.dispatch('logout');
                    });
                } else {
                    this.$store.dispatch('logout');
                }
            }
        }
    });
</script>

<style lang="sass">
.smallButtonPadding button.v-btn
    padding: 0 4px !important

/* Right drawers must not block the top bar (settings, AI Lucy, logout). */
.v-app-bar
    z-index: 12 !important

.v-application--wrap .v-overlay
    z-index: 10 !important

.v-app-bar .brand-logo-btn
    flex-shrink: 0
    overflow: visible

.v-app-bar .v-toolbar__content
    overflow: hidden
    padding-left: 8px
    padding-right: 8px

// Left sidebar is 290px; v-container adds 12px. Toolbar already has 8px padding,
// so this column ends where the workspace below begins.
.app-bar-brand
    display: flex
    align-items: center
    flex: 0 0 auto
    min-width: 0

.app-bar-brand--workspace
    flex: 0 0 calc(290px - 8px + 12px)
    width: calc(290px - 8px + 12px)

.app-bar-main
    min-width: 0
    flex: 1 1 auto
    overflow: hidden

.app-bar-end
    display: flex
    align-items: center
    flex: 0 0 auto
    max-width: 100%
    gap: 8px

// At 1280px the Czech article labels meet the right cluster. Tightening only
// this gap (same items, same order) gives the save button its width back.
@media (max-width: 1366px)
    .app-bar-main:has(.top-bar-article-actions) ~ .app-bar-end
        gap: 4px
    .app-bar-main .top-bar-pill
        padding: 0 14px !important

.lucy-bar-btn
    flex-shrink: 0
    color: #fff !important
    caret-color: #fff !important
    font-weight: 700 !important
    letter-spacing: 0.02em !important
    height: 40px !important

    .v-icon
        color: #fff !important

.lucy-bar-btn__mark
    width: 20px
    height: 20px
    border-radius: 50%
    background: rgba(255, 255, 255, 0.22)
    display: inline-flex
    align-items: center
    justify-content: center
    font-size: 11px
    font-weight: 800
    margin-right: 6px

.lucy-bar-btn__label
    white-space: nowrap

.v-app-bar .app-bar-icon-btn
    width: 40px !important
    height: 40px !important
    min-width: 40px !important
    margin: 0 !important
    flex-shrink: 0

// ─── Login page layout ────────────────────────────────────────────────────────
.login-layout
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    min-height: calc(100vh - 64px)
    padding: 32px 16px

    &--wide
        flex-direction: row
        align-items: flex-start
        justify-content: center
        gap: 48px
        padding: 48px 32px

.login-features-col
    flex: 0 1 480px
    align-self: center

.login-card-col
    flex: 0 0 auto
    width: 100%
    max-width: 420px
    align-self: center

// ─── Login card ───────────────────────────────────────────────────────────────
.login-card
    overflow: hidden !important

    .theme--light &
        background: #ffffff
        box-shadow: 0 8px 40px rgba(58, 53, 65, 0.16) !important

    .theme--dark &
        background: #2a2d3e
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45) !important

.login-card-header
    padding: 32px 36px 28px
    text-align: center

.login-card-tagline
    color: rgba(255,255,255,0.85)
    font-size: 0.875rem
    letter-spacing: 0.01em

.login-card-body
    padding: 28px 36px 36px

    @media (max-width: 480px)
        padding: 20px 20px 28px
</style>
